import React, { useEffect } from "react";
import styles from "./DocumentPreviewModal.module.css";

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string | null;
  description?: string;
}

const isImageUrl = (url?: string | null): boolean => {
  if (!url) return false;
  return (
    /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url) ||
    url.startsWith("data:image/") ||
    url.includes("/image/")
  );
};

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  title,
  url,
  description,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isImage = isImageUrl(url);
  const hasUrl = Boolean(url && url.trim().length > 0);

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-preview-title"
    >
      <div className={styles.modalCard}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleWrap}>
            <div className={styles.docBadgeIcon}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div className={styles.headerTextGroup}>
              <h3 id="document-preview-title" className={styles.docTitle}>
                {title}
              </h3>
              <span className={styles.docSubtitle}>
                {isImage ? "Image Document" : "PDF Document"}
              </span>
            </div>
          </div>

          <div className={styles.headerActions}>
            {hasUrl && url && (
              <a
                href={url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className={styles.downloadBtn}
                title="Download Document"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Download</span>
              </a>
            )}
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close document preview"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="18" x2="18" y2="6"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {hasUrl && isImage ? (
            <div className={styles.imageContainer}>
              <img src={url!} alt={title} className={styles.previewImage} />
            </div>
          ) : hasUrl ? (
            <iframe
              src={`${url!}#toolbar=1&navpanes=1`}
              title={title}
              className={styles.previewFrame}
            />
          ) : (
            <div className={styles.placeholderCard}>
              <div className={styles.placeholderIconWrap}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
              </div>
              <span className={styles.placeholderStatusPill}>
                Filed with County &middot; Registered
              </span>
              <h4 className={styles.placeholderTitle}>{title}</h4>
              <p className={styles.placeholderDesc}>
                {description ||
                  "This document has been filed and registered for review. The full scanned digital archive is syncing with county records."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
